import { Controller } from "@hotwired/stimulus"

// Een formulier dat één ding tegelijk vraagt. Een beantwoorde stap wordt een
// regel met je antwoord en "Wijzigen"; de eerstvolgende onbeantwoorde stap
// staat open; wat daarna nog niet beantwoord is, staat er nog niet.
//
// Zonder JavaScript gebeurt hier niets en staat het hele formulier gewoon open:
// de controller zet de ingeklapte stand zelf, hij staat niet in de HTML.
export default class extends Controller {
  static targets = ["stap", "afronden"]
  // De woorden komen uit de locale-bestanden, niet uit deze controller.
  static values = { woorden: { type: Object, default: { tot: " tot ", en: " en ", pauze: "%{minutes} min pauze", vandaag: "vandaag", gisteren: "gisteren" } } }

  connect() {
    // Een bestaande registratie bewerk je meestal op één ding; dan helpt het
    // niet om alles dicht te zetten. Hetzelfde geldt als er een fout staat.
    if (this.element.dataset.stappenAllesOpen === "true") return

    this.stapTargets.forEach((stap) => this.rust(stap))
    this.openEerste()
  }

  // Een stap opent, de rest sluit. Ook aangeroepen vanaf "Wijzigen".
  open(event) {
    const stap = event.currentTarget.closest("[data-stappen-target='stap']")
    this.toon(stap)
    const eerste = stap.querySelector("input:not([type=hidden]):not([disabled]), select, textarea, button[data-combobox-target='input']")
    eerste?.focus({ preventScroll: true })
    stap.scrollIntoView({ behavior: this.beweging, block: "center" })
  }

  // Na het invullen van een veld: is deze stap klaar, dan naar de volgende.
  volgende(event) {
    // Zoektekst is nog geen keuze. Het zoekveld verliest zijn focus zodra je op
    // een optie tikt, en dan klapte de stap dicht voordat die tik aankwam.
    if (event.target.dataset.comboboxTarget === "input") return

    const stap = event.target.closest("[data-stappen-target='stap']")
    if (!stap || !this.ingevuld(stap)) return
    // De tijd is pas af als jij het zegt: na het eerste tijdvak kan er nog een
    // tweede of een pauze bij. Die stap klapt dus niet vanzelf dicht.
    if (stap.dataset.stappenVorm === "tijd") return

    this.rust(stap)
    this.openEerste({ na: stap })
  }

  // Staat alles al ingevuld, dan blijft alles dicht: dan is dit scherm een
  // bevestiging en geen invuloefening.
  openEerste({ na } = {}) {
    const stappen = na ? this.stapTargets.slice(this.stapTargets.indexOf(na) + 1) : this.stapTargets
    const doel = stappen.find((stap) => !this.ingevuld(stap) || (na && this.vraagtNogKeuze(stap)))
    if (doel) this.toon(doel)
    else if (na) this.alleenNogBevestigen(stappen)
  }

  // Alles na deze stap stond al ingevuld, bijvoorbeeld de tijden na Klaar op de
  // timer. Dan valt er niets meer te vragen: de rest als regels, en Opslaan.
  alleenNogBevestigen(stappen) {
    stappen.forEach((stap) => this.rust(stap))
    if (!this.hasAfrondenTarget) return

    this.afrondenTarget.hidden = false
    // Midden, niet onderaan: daar ligt de onderbalk over de knop heen.
    const opslaan = this.afrondenTarget.querySelector("[type=submit]") || this.afrondenTarget
    opslaan.scrollIntoView({ behavior: this.beweging, block: "center" })
  }

  // "Geen project" staat standaard ingevuld en telt dus als antwoord. Heeft de
  // opdrachtgever die je net koos projecten, dan hoort de vraag toch te komen,
  // tot je zelf een blobje aantikt.
  vraagtNogKeuze(stap) {
    const keuzes = stap.querySelector("[data-controller~='keuzeknoppen']")
    const bron = document.getElementById(keuzes?.dataset.keuzeknoppenFilterveld || "")
    if (!bron?.value) return false
    // Een net ingetypte opdrachtgever heeft nog geen projecten, maar wie ook
    // een project mag intypen, moet die vraag wel krijgen.
    if (bron.value === "__new__") return Boolean(stap.querySelector("[data-combobox-new-enabled-value='true']"))

    const knoppen = [...stap.querySelectorAll("[data-keuzeknoppen-target='knop']")]
    if (knoppen.some((knop) => knop.getAttribute("aria-pressed") === "true")) return false
    return knoppen.some((knop) => knop.dataset.filter === bron.value)
  }


  toon(stap) {
    this.stapTargets.forEach((andere) => {
      if (andere !== stap) this.rust(andere)
    })
    this.verbergWatNogNietAanDeBeurtIs(stap)
    this.toonAfrondenAlsDeTijdInBeeldIs()
    stap.dataset.stand = "open"
    this.inhoud(stap).hidden = false
    const samenvatting = this.samenvatting(stap)
    if (samenvatting) samenvatting.hidden = true
  }

  // Dicht: een regel met het antwoord, of een grijze regel als er nog niets staat.
  rust(stap) {
    const samenvatting = this.samenvatting(stap)
    if (!samenvatting) return

    stap.hidden = false

    const waarde = this.antwoord(stap)
    stap.dataset.stand = waarde ? "klaar" : "wacht"
    samenvatting.hidden = false
    samenvatting.querySelector("[data-stappen-waarde]").textContent = waarde || samenvatting.dataset.leeg || ""
    this.inhoud(stap).hidden = true
  }

  // Wat nog niet aan de beurt is, staat er niet. Eerst kies je de opdrachtgever,
  // dan pas komen de projecten tevoorschijn: één vraag per keer, en geen muur
  // van velden waar je doorheen moet.
  verbergWatNogNietAanDeBeurtIs(open) {
    let voorbij = false
    this.stapTargets.forEach((stap) => {
      if (stap === open) {
        voorbij = true
        stap.hidden = false
        return
      }
      // Ook wat al een standaard heeft (vandaag, geen project): anders staat
      // het antwoord op vraag drie er al voor je vraag twee hebt gezien.
      stap.hidden = voorbij
    })
  }

  // Opslaan pas als de tijd in beeld is: eerder zijn er nog vragen open.
  toonAfrondenAlsDeTijdInBeeldIs() {
    if (!this.hasAfrondenTarget) return

    const tijd = this.stapTargets.find((stap) => stap.dataset.stappenVorm === "tijd")
    this.afrondenTarget.hidden = Boolean(tijd?.hidden)
  }

  ingevuld(stap) {
    return this.antwoord(stap) !== ""
  }

  // Wat er in de regel komt te staan. Een keuzelijst toont zijn gekozen tekst,
  // een datum zijn eigen weergave, en een stap mag zelf een tekst aanleveren
  // via data-stappen-tekst (bijvoorbeeld "2 tijdvakken, 3,5 uur").
  antwoord(stap) {
    if (stap.dataset.stappenTekst) return stap.dataset.stappenTekst
    if (stap.dataset.stappenVorm === "tijd") return this.tijdTekst(stap)
    if (stap.dataset.stappenVorm === "datum") return this.datumTekst(stap)

    const velden = [...stap.querySelectorAll("input:not([type=hidden]), select, textarea")]
      .filter((veld) => veld.type !== "submit" && veld.type !== "button")
    const gevuld = velden.map((veld) => this.tekstVan(veld)).filter((tekst) => tekst !== "")
    return gevuld.join(" · ")
  }

  // "08:00 tot 16:30 · 30 min pauze", en bij meer blokken met "en" ertussen.
  tijdTekst(stap) {
    const blokken = [...stap.querySelectorAll("[data-time-blocks-target='block']")]
      .filter((blok) => !blok.hidden)
      .map((blok) => [...blok.querySelectorAll("input[type='time']")].map((veld) => this.klok(veld.value)))
      .filter(([van, tot]) => van && tot)
      .map(([van, tot]) => `${van}${this.woordenValue.tot}${tot}`)
    if (blokken.length === 0) return ""

    const pauze = stap.querySelector("input[name*='break_minutes']")?.value
    const pauzeTekst = pauze && Number(pauze) > 0 ? ` · ${this.woordenValue.pauze.replace("%{minutes}", pauze)}` : ""
    return blokken.join(this.woordenValue.en) + pauzeTekst
  }

  datumTekst(stap) {
    const waarde = stap.querySelector("input[type='date']")?.value
    if (!waarde) return ""

    const datum = new Date(`${waarde}T00:00:00`)
    const vandaag = new Date()
    vandaag.setHours(0, 0, 0, 0)
    const verschil = Math.round((datum - vandaag) / 86400000)
    if (verschil === 0) return this.woordenValue.vandaag
    if (verschil === -1) return this.woordenValue.gisteren

    return datum.toLocaleDateString(document.documentElement.lang || "nl", { day: "numeric", month: "long" })
  }

  klok(waarde) {
    return (waarde || "").slice(0, 5)
  }

  tekstVan(veld) {
    if (veld.type === "checkbox" || veld.type === "radio") return veld.checked ? veld.labels?.[0]?.textContent.trim() || "" : ""
    if (veld.dataset.comboboxTarget === "input") return veld.value.trim()
    return (veld.value || "").trim()
  }

  inhoud(stap) {
    return stap.querySelector("[data-stappen-target='inhoud']")
  }

  samenvatting(stap) {
    return stap.querySelector("[data-stappen-target='samenvatting']")
  }

  get beweging() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth"
  }
}
